import React, { useState } from 'react';
import { formatEuroNumber, parseEuroNumber } from '@/utils/locale-format';

interface Props {
  value: string | number | null | undefined;
  onChange: (val: string | null) => void;
  className?: string;
  'data-test-id'?: string;
}

/**
 * PIK Balance input: shows "-" if empty/null/0, else formatted currency
 */
export default function FormattedPIKInput({
  value,
  onChange,
  className,
  'data-test-id': dataTestId,
}: Props) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Convert to number for display
  const numValue = React.useMemo(() => {
    if (value === null || value === undefined || value === '' || value === '-') return null;
    if (typeof value === 'string') {
      const cleaned = value.trim();
      if (cleaned === '' || cleaned === '-') return null;
      const parsed = parseEuroNumber(cleaned);
      return parsed;
    }
    return Number(value);
  }, [value]);

  // Display "-" if empty/null/0, else formatted currency
  const displayValue = React.useMemo(() => {
    if (numValue === null || numValue === 0) return '-';
    return formatEuroNumber(numValue, 2);
  }, [numValue]);

  const handleInput = (txt: string) => {
    if (txt.trim() === '' || txt.trim() === '-') {
      onChange(null);
      return;
    }
    const num = parseEuroNumber(txt);
    if (num !== null) {
      onChange(String(num));
    } else {
      onChange(null);
    }
  };

  // Show raw value when focused, formatted when blurred
  const inputValue = isFocused 
    ? (numValue !== null && numValue !== 0 ? numValue.toString() : '')
    : displayValue;

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <input
        type="text"
        className={`${className || ''} rounded-md shadow-sm h-10 py-2`}
        value={inputValue}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder="-"
        data-test-id={dataTestId}
        style={{ 
          width: '100%',
          paddingLeft: '12px',
          paddingRight: (isHovered && numValue !== null && numValue !== 0) ? '14px' : '12px',
        }}
      />
      {/* Show spinner controls only on hover for currency values */}
      {isHovered && numValue !== null && numValue !== 0 && (
        <div className="absolute right-0 top-0 bottom-0 flex flex-col pointer-events-none" style={{ width: '14px' }}>
          <button
            type="button"
            className="flex-1 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors pointer-events-auto"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const step = 1000;
              onChange(String((numValue || 0) + step));
            }}
            aria-label="increment"
            style={{
              fontSize: '8px',
              lineHeight: '1',
              color: '#6b7280',
              background: '#f3f4f6',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            ▲
          </button>
          <button
            type="button"
            className="flex-1 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors pointer-events-auto"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const step = 1000;
              const newVal = (numValue || 0) - step;
              onChange(newVal <= 0 ? null : String(newVal));
            }}
            aria-label="decrement"
            style={{
              fontSize: '8px',
              lineHeight: '1',
              color: '#6b7280',
              background: '#f3f4f6',
            }}
          >
            ▼
          </button>
        </div>
      )}
    </div>
  );
}

